import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Box, Typography } from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

import FormErrorUI from './FormErrorUI';
import FormLabelUI from './FormLabelUi';

const defaultModules = {
  toolbar: [
    [{ font: [] }],
    [{ header: [1, 2, 3, false] }, { size: ['small', false, 'large', 'huge'] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ color: [] }, { background: [] }],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ align: [] }],
    ['blockquote', 'code-block'],
    ['link'],
    ['clean'],
  ],
};

const defaultFormats = [
  'font',
  'header',
  'size',
  'bold',
  'italic',
  'underline',
  'strike',
  'color',
  'background',
  'list',
  'bullet',
  'align',
  'blockquote',
  'code-block',
  'link',
];

const RichTextEditorUI = forwardRef(({
  className,
  error,
  formats = defaultFormats,
  height,
  helperText,
  label,
  minHeight = 320,
  modules = defaultModules,
  onBlur,
  onChange,
  onFocus,
  placeholder,
  readOnly = false,
  value = '',
}, ref) => {
  const quillRef = useRef(null);
  const resolvedHeight = height ?? minHeight;

  useImperativeHandle(ref, () => ({
    focus: () => {
      quillRef.current?.focus?.();
    },
    getEditor: () => {
      return quillRef.current?.getEditor?.() ?? null;
    },
    insertText: (text) => {
      const editor = quillRef.current?.getEditor?.();

      if (!editor || !text) return;

      const range = editor.getSelection(true) ?? {
        index: editor.getLength(),
        length: 0,
      };

      editor.insertText(range.index, text, 'user');
      editor.setSelection(range.index + text.length, 0, 'silent');
      quillRef.current?.focus?.();
    },
  }));

  return (
    <Box className={className}>
      {label && <Box sx={{ mb: 1 }}>{typeof label === 'string' ? <FormLabelUI text={label} /> : label}</Box>}
      <Box
        className="rich-text-editor-ui"
        sx={{
          pointerEvents: readOnly ? 'none' : 'auto',
          opacity: readOnly ? 0.8 : 1,
        }}
        style={{
          '--editor-height': typeof resolvedHeight === 'number' ? `${resolvedHeight}px` : resolvedHeight,
        }}
      >
        <ReactQuill
          ref={quillRef}
          theme="snow"
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          modules={modules}
          formats={formats}
          readOnly={readOnly}
          placeholder={placeholder}
          style={{ height: resolvedHeight }}
        />
      </Box>
      <style>
        {`
          .rich-text-editor-ui .ql-container {
            height: calc(var(--editor-height) - 42px);
            overflow: hidden;
          }
          .rich-text-editor-ui .ql-editor {
            height: 100%;
            overflow-y: auto;
          }
        `}
      </style>
      {error && (
        <Box sx={{ mt: 1 }}>
          <FormErrorUI message={error} />
        </Box>
      )}
      {helperText && (
        <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
          {helperText}
        </Typography>
      )}
    </Box>
  );
});

export default RichTextEditorUI;
